/// <amd-module name="VSS/Features/Subscriptions/Contexts/SubscriptionContext" />
import { ISubscriptionAccount } from "Contracts/Commerce";
import { Subscription } from "Models/Subscription";
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { Uri } from "VSS/Platform/Util/Url";

// exported for use in clientUserProvider and serverUserProvider
const SubscriptionContext = React.createContext<ISubscriptionContextValue>(undefined as never);

export interface ISubscriptionContextValue {
    state: {
        subscriptions?: Subscription[];
        organizationSubscription?: Subscription;
        loadedAllSubscriptions: boolean;
        subscriptionIdToSubscription?: { [subscriptionId: string]: Subscription };
    };
    actions: {
        redirectToCreateSubscription: (replyTo?: string, redirectQueryParams?: { [key: string]: string }) => void;
        validateSubscription: (subscriptionId: string, hostId?: string) => Promise<boolean>;
    };
}

export interface ISubscriptionProviderProps {
    pageContext: IVssPageContext;
}

interface ISubscriptionProviderInternalProps extends ISubscriptionProviderProps,
    Pick<ISubscriptionContextValue["actions"], "redirectToCreateSubscription"> {
    getSubscriptions: SubscriptionProvider["getSubscriptions"];
    getOrganizationSubscription: SubscriptionProvider["getOrganizationSubscription"];
    validateSubscription: SubscriptionProvider["validateSubscription"];
}

interface ISubscriptionProviderInternalState extends Pick<ISubscriptionContextValue["state"],
    "subscriptions" | "organizationSubscription" | "loadedAllSubscriptions"> {
}

class SubscriptionProviderInternal extends React.Component<ISubscriptionProviderInternalProps, ISubscriptionProviderInternalState> {

    private static subscriptionsToDictionary(subscriptions: Subscription[] = []) {
        const result: { [sub: string]: Subscription } = {};
        subscriptions.forEach((sub) => result[sub.id] = sub);
        return result;
    }
    private initPromise: Promise<{
        allSubscriptions: Subscription[],
        organizationSubscription: Subscription | undefined,
    }>;
    private mounted: boolean;

    constructor(props: ISubscriptionProviderInternalProps) {
        super(props);

        this.state = {
            loadedAllSubscriptions: false,
        };

        this.initPromise = this.getInitPromise();
    }

    public async componentDidMount(): Promise<void> {
        this.mounted = true;
        const initState = await this.initPromise;
        if (this.mounted) {
            this.setState({
                subscriptions: initState.allSubscriptions,
                organizationSubscription: initState.organizationSubscription,
                loadedAllSubscriptions: true,
            });
        }
    }

    public componentWillUnmount(): void {
        this.mounted = false;
    }

    public render(): JSX.Element | null {
        const { redirectToCreateSubscription } = this.props;
        return (
            <SubscriptionContext.Provider
                value={{
                    actions: {
                        redirectToCreateSubscription,
                        validateSubscription: this.validateSubscription,
                    },
                    state: {
                        ...this.state,
                        subscriptionIdToSubscription: SubscriptionProviderInternal.subscriptionsToDictionary(this.state.subscriptions),
                    },
                }}
            >
                {this.props.children}
            </SubscriptionContext.Provider>);
    }

    private getInitPromise = async (): Promise<{ allSubscriptions: Subscription[], organizationSubscription: Subscription | undefined }> => {
        const allSubscriptions = await this.getSubscriptions();
        const organizationSubscription = await this.getOrganizationSubscription();
        return {
            allSubscriptions,
            organizationSubscription,
        };
    }

    private validateSubscription = async (subscriptionId: string, orgId?: string): Promise<boolean> => {
        const result = await this.props.validateSubscription(subscriptionId, orgId);
        if (result) {
            this.setState((prevState) => {
                if (prevState.subscriptions) {
                    const newList = ([] as Subscription[]).concat(prevState.subscriptions.map((sub) =>
                        sub.id === subscriptionId && result
                            ? new Subscription({
                                ...sub.subscriptionAccount,
                                failedPurchaseReason: result.failedPurchaseReason,
                            }, true)
                            : sub));
                    return {
                        subscriptions: newList,
                    };
                }
                return {};
            });
            return new Subscription(result, true).isUsable;
        }

        return false;
    }

    private getSubscriptions = async (): Promise<Subscription[]> => {
        const subscriptionAccounts = await this.props.getSubscriptions();
        return subscriptionAccounts.map((subscriptionAccount) => new Subscription(subscriptionAccount));
    }

    private getOrganizationSubscription = async (): Promise<Subscription | undefined> => {
        const subscriptionAccount = await this.props.getOrganizationSubscription();
        if (subscriptionAccount) {
            return new Subscription(subscriptionAccount);
        }
        return undefined;
    }
}

export class SubscriptionProvider extends React.Component<ISubscriptionProviderProps, {}> {
    private pageData: IPageData | undefined;

    constructor(props: ISubscriptionProviderProps) {
        super(props);

        this.pageData = props.pageContext.getService<IVssPageService>("IVssPageService").getData();
    }

    public render(): JSX.Element {
        return (
            <SubscriptionProviderInternal
                {...this.props}
                getSubscriptions={this.getSubscriptions}
                getOrganizationSubscription={this.getOrganizationSubscription}
                redirectToCreateSubscription={this.redirectToCreateSubscription}
                validateSubscription={this.validateSubscription}
            />);
    }

    private validateSubscription = async (subscriptionId: string, orgId?: string): Promise<ISubscriptionAccount | undefined> => {
        const hostId = orgId || (this.pageData && this.pageData.hostId);
        const contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");

        await new Promise((resolve) => setTimeout(resolve, 3000));

        const isValidResult = await contributionService.getDataAsync<ISubscriptionAccount>(
            "ms.vss-features.validate-subscription-data-provider",
            {
                orgId: hostId,
                subscriptionId,
            },
            true,
        );
        return isValidResult;
    }

    private getOrganizationSubscription = async (orgId?: string): Promise<ISubscriptionAccount | undefined> => {
        const hostId = orgId || (this.pageData && this.pageData.hostId);
        const contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<ISubscriptionAccount | undefined>(
            "ms.vss-features.get-host-subscription-data-provider",
            { orgId: hostId },
            true,
        );
    }

    private getSubscriptions = async (): Promise<ISubscriptionAccount[]> => {
        const contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
        const result = await contributionService.getDataAsync<ISubscriptionAccount[]>(
            "ms.vss-features.get-subscriptions-data-provider",
            {},
            true);
        return result || [];
    }

    private redirectToCreateSubscription(replyTo?: string, redirectQueryParams: { [key: string]: string } = {}) {
        const createReplyTo = new Uri(replyTo || window.location.href);
        if (!replyTo) {
            createReplyTo.addQueryParams(redirectQueryParams);
        }

        const subscriptionCreationUri = new Uri("https://account-staging.azure.com/signup");
        subscriptionCreationUri.addQueryParam("appId", "101", true);
        subscriptionCreationUri.addQueryParam("offer", "MS-AZR-0003P", true);
        subscriptionCreationUri.addQueryParam("recommend", "0", true);
        subscriptionCreationUri.addQueryParam("noPrompt", "true", true);
        subscriptionCreationUri.addQueryParam("redirectURL", encodeURI(createReplyTo.absoluteUri), true);
        window.location.href = subscriptionCreationUri.absoluteUri;
    }
}

export const SubscriptionConsumer = SubscriptionContext.Consumer;
